import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { useIntl } from "react-intl";
import { useCrm_task_statuses } from "../../../hooks";
import {
  completeTask,
  cancelRecord,
  deleteRecord,
} from "../../../features/crm/activities/activitiesSlice";
import { deleteConfirmMessage } from "../../forms/formsFeedback";
import { Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";

const useFeedItem = (record, entity, entityId, branchId, reload) => {
  const { task, event, id } = record;
  const [modal, contextHolder] = Modal.useModal();
  const intl = useIntl();
  const dispatch = useDispatch();
  const profileId = useSelector((state) => state.user.profile.user);
  const statuses = useCrm_task_statuses();
  const completeStatusId =
    statuses &&
    statuses.length > 0 &&
    statuses.filter((i) => i.name === "completed")[0].id;

  const canceledStatusId =
    statuses &&
    statuses.length > 0 &&
    statuses.filter((i) => i.name === "canceled")[0].id;

  const show_complete_button =
    (task && task.status_id !== completeStatusId) || false;
  const show_cancel_button =
    task && task.type === "task" && task.status_id !== canceledStatusId;
  const show_delete_button =
    !(task && task.type === "task") && !record.is_deleted;

  const onCompleteTask = async () => {
    const completeStatusId = statuses.filter((i) => i.name === "completed")[0]
      .id;

    const activity = {
      id: task.id,
      status_id: completeStatusId,
      due_date: moment().format(),
    };

    const params = {
      activity,
      entity,
      entityId,
      activityType: "tasks",
      scope: `branches/${branchId}`,
    };

    const action = await dispatch(completeTask(params));
    if (!action.error) {
      reload();
    }
  };

  const onCancelActivity = () => {
    const subject = task ? task.subject : event.subject;
    const title = intl.formatMessage({
      id: "app.forms.activities.cancelConfirmTitle",
    });
    const content =
      intl.formatMessage({
        id: "app.forms.activities.cancelConfirmContent",
      }) +
      " " +
      subject +
      "?";
    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk() {
        return onConfirm(record);
      },
      onCancel() {},
    });
  };

  const onConfirm = async () => {
    const cancelStatusId = statuses.filter((i) => i.name === "canceled")[0].id;
    const activityType = task ? "tasks" : "events";
    const activityId = task ? record.task_id : record.event_id;
    const activity = {
      id: activityId,
      status_id: cancelStatusId,
    };

    const params = {
      activity,
      entity,
      entityId,
      activityType,
      scope: branchId ? `/branches/${branchId}` : "",
    };

    const action = await dispatch(cancelRecord(params));
    if (!action.error) {
      reload();
    }
  };

  const onDeleteActivity = () => {
    deleteConfirmMessage(
      intl,
      task ? task.subject : event.subject,
      "activities",
      record,
      onConfirmDelete,
      modal
    );
  };

  const onConfirmDelete = async () => {
    const activityType = task ? "tasks" : "events";
    const activityId = task ? record.task_id : record.event_id;
    const activity = {
      id: activityId,
      is_deleted: true,
    };
    const params = {
      activity,
      entity,
      entityId,
      activityType,
      scope: branchId ? `/branches/${branchId}` : "",
    };

    const action = await dispatch(deleteRecord(params));
    if (!action.error) {
      reload();
    }
  };

  const isPast = () => {
    const due_date = task ? task.due_date : null;
    const start_time = event ? event.start_time : null;
    return moment() > moment(due_date) || moment() > moment(start_time);
  };

  const isSameUser = () => {
    const userId = task ? record.task.assigned_to_id : record.event.owner_id;
    return profileId === userId;
  };

  const getActivityUserName = () => {
    const userName = task
      ? record.task.assigned_to.name
      : record.event.owner.name;
    return userName;
  };

  const actionText = () => {
    let text = "";
    const type = task ? task.type : "event";

    if (isSameUser()) {
      text = `${intl.formatMessage({
        id: `app.activities.${type}_self_action_${
          isPast() ? "past" : "future"
        }`,
      })} ${intl.formatMessage({ id: `app.activities.${type}` })}`;
    } else {
      text = `${intl.formatMessage({
        id: `app.activities.${type}_other_action_start_${
          isPast() ? "past" : "future"
        }`,
      })}${getActivityUserName()} ${intl.formatMessage({
        id: `app.activities.${type}_other_action_end_${
          isPast() ? "past" : "future"
        }`,
      })} ${intl.formatMessage({ id: `app.activities.${type}` })}`;
    }

    return text;
  };
  return {
    modal,
    contextHolder,
    show_complete_button,
    show_cancel_button,
    show_delete_button,
    onCompleteTask,
    onCancelActivity,
    onDeleteActivity,
    onConfirm,
    isPast,
    isSameUser,
    getActivityUserName,
    actionText,
  };
};

export default useFeedItem;
